import { useState } from 'react';
import { CheckIcon, InformationCircleIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import type { AlignerOption } from './types';

interface AlignerSectionProps {
    aligner: AlignerOption;
    onAlignerChange: (aligner: AlignerOption) => void;
    addOrthopulse: boolean;
    onOrthopulseChange: (add: boolean) => void;
    ack: boolean;
    onAckChange: (ack: boolean) => void;
    showOrthopulse: boolean;
    /** Fully formatted, e.g. "$425". Null when the practice's catalog has no
     *  resolved price, in which case no price is shown at all. */
    orthopulsePriceLabel: string | null;
    /** Dx-report recommendation level. 'mandatory' locks the toggle on;
     *  'recommended'/null only set the default and badge. null = no badge. */
    orthopulseLevel: 'mandatory' | 'recommended' | null;
}

interface AlignerOptionMeta {
    value: 'active' | 'starter';
    label: string;
    sub: string;
}

const ALIGNER_OPTIONS: AlignerOptionMeta[] = [
    {
        value: 'starter',
        label: 'Starter aligners',
        sub: 'Attachment template + 3 starter aligners'
    },
    {
        value: 'active',
        label: 'Active aligners',
        sub: 'Standard delivery: attachment template + active aligners'
    }
];

/**
 * Sidebar aligner picker + Orthopulse toggle + acknowledgement.
 * The page-level approve button reads (aligner !== null && ack).
 */
export function AlignerSection({
    aligner,
    onAlignerChange,
    addOrthopulse,
    onOrthopulseChange,
    ack,
    onAckChange,
    showOrthopulse,
    orthopulsePriceLabel,
    orthopulseLevel
}: AlignerSectionProps) {
    const [showInfo, setShowInfo] = useState(false);
    const isMandatory = orthopulseLevel === 'mandatory';

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col flex-shrink-0">
            <div className="flex items-center justify-between gap-3 mb-4">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 flex-1 min-w-0">
                    Select an aligner option
                </div>
                <button
                    type="button"
                    onClick={() => setShowInfo((s) => !s)}
                    className="flex items-center gap-1 text-xs font-semibold text-yellow-700 hover:text-yellow-800 transition-colors flex-shrink-0 whitespace-nowrap"
                >
                    <InformationCircleIcon className="w-3.5 h-3.5" />
                    What is a starter aligner?
                </button>
            </div>

            {showInfo && (
                <div className="text-xs text-gray-700 leading-relaxed bg-yellow-50 border border-yellow-200 rounded-lg px-3.5 py-3 mb-3 space-y-2">
                    <p className="m-0">
                        Full-series trays are built from the simulation, so any bonding-day flash or
                        attachment shift only surfaces later as a premature refinement - the{' '}
                        <strong className="font-semibold text-gray-900">
                            #1 reason cases run long
                        </strong>
                        .
                    </p>
                    <p className="m-0">
                        With Starter Aligners, we ship the{' '}
                        <strong className="font-semibold text-gray-900">
                            attachment template + 3 starter trays
                        </strong>{' '}
                        first. You bond, send a quick follow-up scan, and we build the active
                        aligners around the <em>actual</em> attachment positions and any minor
                        irregularities.
                    </p>
                </div>
            )}

            <div className="flex flex-col gap-2.5 mb-4">
                {ALIGNER_OPTIONS.map(({ value, label, sub }) => {
                    const selected = aligner === value;
                    return (
                        <button
                            type="button"
                            key={value}
                            onClick={() => onAlignerChange(value)}
                            className={`text-left flex items-start gap-3 px-3.5 py-3 rounded-lg border transition-colors ${
                                selected
                                    ? 'border-yellow-400 bg-yellow-50'
                                    : 'border-gray-200 bg-white hover:border-gray-400'
                            }`}
                            style={{ borderWidth: '1.5px' }}
                        >
                            <RadioDot selected={selected} />
                            <div className="flex-1 min-w-0">
                                <div
                                    className={`text-sm font-semibold ${selected ? 'text-gray-900' : 'text-gray-700'}`}
                                >
                                    {label}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 leading-snug">
                                    {sub}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {showOrthopulse && (
                <>
                    <div className="h-px bg-gray-200 mb-4" />
                    <button
                        type="button"
                        onClick={isMandatory ? undefined : () => onOrthopulseChange(!addOrthopulse)}
                        disabled={isMandatory}
                        className={`flex items-center gap-3 px-3.5 py-3 rounded-lg border mb-4 transition-colors text-left ${
                            isMandatory
                                ? 'bg-yellow-50 border-yellow-400 cursor-not-allowed'
                                : addOrthopulse
                                  ? 'bg-yellow-50 border-yellow-400 cursor-pointer'
                                  : 'bg-gray-50 border-gray-200 hover:border-gray-300 cursor-pointer'
                        }`}
                        style={{ borderWidth: '1.5px' }}
                    >
                        <ToggleSwitch on={addOrthopulse} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-semibold text-gray-900">
                                    Add Orthopulse
                                </span>
                                {orthopulseLevel && <OrthopulseBadge level={orthopulseLevel} />}
                                {isMandatory && (
                                    <LockClosedIcon className="w-3 h-3 text-gray-500 flex-shrink-0" />
                                )}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                                Optimize bone metabolism for enhanced movement
                                {orthopulsePriceLabel && ` · +${orthopulsePriceLabel}`}
                            </div>
                        </div>
                    </button>
                </>
            )}

            <div className="h-px bg-gray-200 mb-4" />

            <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-700 leading-relaxed mb-3 m-0">
                    Please make sure you have reviewed the Diagnostic Report and case design
                    provided before approving. By clicking approve below, you are approving the case
                    as designed, and it will be subsequently sent to manufacturing. Aligners are
                    custom to the patient and non-returnable.
                </p>
                <button
                    type="button"
                    onClick={() => onAckChange(!ack)}
                    className="flex items-start gap-2.5 cursor-pointer text-left"
                >
                    <span
                        className={`w-4 h-4 rounded flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                            ack ? 'bg-yellow-400 border-yellow-500' : 'bg-white border-gray-400'
                        }`}
                        style={{ borderWidth: '1.5px', borderStyle: 'solid' }}
                    >
                        {ack && <CheckIcon className="w-2.5 h-2.5 text-gray-900" strokeWidth={3} />}
                    </span>
                    <span className="text-xs text-gray-700 font-medium leading-snug">
                        I acknowledge the above information and approve the case for manufacturing.
                    </span>
                </button>
            </div>
        </div>
    );
}

function OrthopulseBadge({ level }: { level: 'mandatory' | 'recommended' }) {
    const isMandatory = level === 'mandatory';
    return (
        <span
            className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                isMandatory ? 'bg-yellow-400 text-gray-900' : 'bg-gray-100 text-gray-600'
            }`}
        >
            {isMandatory ? 'Mandatory' : 'Recommended'}
        </span>
    );
}

function RadioDot({ selected }: { selected: boolean }) {
    return (
        <span
            className={`w-4 h-4 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                selected ? 'border-yellow-500 bg-yellow-400' : 'border-gray-400 bg-white'
            }`}
            style={{ borderWidth: '2px', borderStyle: 'solid' }}
        >
            {selected && <span className="w-1.5 h-1.5 rounded-full bg-gray-900" />}
        </span>
    );
}

function ToggleSwitch({ on }: { on: boolean }) {
    return (
        <span
            className={`w-8 h-[18px] rounded-full flex-shrink-0 relative transition-colors ${
                on ? 'bg-yellow-400' : 'bg-gray-400'
            }`}
        >
            <span
                className="absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white shadow transition-all"
                style={{ left: on ? '16px' : '2px' }}
            />
        </span>
    );
}
